/**
 * Email Templates
 * 
 * HTML templates for transactional and marketing emails
 */

interface EmailTemplateData {
    userName?: string;
    avisoTitulo?: string;
    avisoLink?: string;
    razon?: string;
    monto?: number;
    concepto?: string;
    resetLink?: string;
    content?: string;
    unsubscribeLink?: string;
    // Store Access Specific
    storeName?: string;
    requesterName?: string;
    requesterEmail?: string;
    whatsapp?: string;
    message?: string;
}

// Base template with consistent styling
const baseTemplate = (title: string, content: string, footer: string = '') => `
<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${title}</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Arial', 'Helvetica', sans-serif; background-color: #f4f4f4;">
    <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px 0;">
        <tr>
            <td align="center">
                <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                    <!-- Header -->
                    <tr>
                        <td style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px 40px; text-align: center;">
                            <h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: bold;">
                                PublicaloGratis
                            </h1>
                            <p style="margin: 8px 0 0 0; color: #ffffff; font-size: 14px; opacity: 0.9;">
                                Tu plataforma de avisos clasificados
                            </p>
                        </td>
                    </tr>
                    
                    <!-- Content -->
                    <tr>
                        <td style="padding: 40px;">
                            ${content}
                        </td>
                    </tr>
                    
                    <!-- Footer -->
                    <tr>
                        <td style="background-color: #f8f9fa; padding: 30px 40px; text-align: center; border-top: 1px solid #e9ecef;">
                            ${footer || `
                                <p style="margin: 0 0 10px 0; color: #6c757d; font-size: 14px;">
                                    © ${new Date().getFullYear()} PublicaloGratis. Todos los derechos reservados.
                                </p>
                                <p style="margin: 0; color: #6c757d; font-size: 12px;">
                                    Este es un email automático, por favor no responder.
                                </p>
                            `}
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>
`;

// Button component
const button = (text: string, link: string, color: string = '#667eea') => `
<table cellpadding="0" cellspacing="0" style="margin: 20px 0;">
    <tr>
        <td align="center" style="border-radius: 6px; background-color: ${color};">
            <a href="${link}" target="_blank" style="display: inline-block; padding: 14px 32px; font-size: 16px; color: #ffffff; text-decoration: none; font-weight: 600;">
                ${text}
            </a>
        </td>
    </tr>
</table>
`;

export const emailTemplates = {
    /**
     * Welcome email for new users
     */
    welcome: (data: EmailTemplateData) => {
        const content = `
            <h2 style="color: #333; margin: 0 0 20px 0;">¡Bienvenido ${data.userName}! 🎉</h2>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Gracias por registrarte en <strong>PublicaloGratis</strong>, la plataforma líder de avisos clasificados en Argentina.
            </p>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Ahora podés:
            </p>
            <ul style="color: #666; font-size: 16px; line-height: 1.8; margin: 0 0 20px 20px;">
                <li>Publicar avisos clasificados <strong>gratis</strong></li>
                <li>Crear tu propia tienda online</li>
                <li>Destacar tus productos y servicios</li>
                <li>Conectar con miles de compradores</li>
            </ul>
            ${button('Empezar a Publicar', 'https://publicalogratis.com/clasificados')}
            <p style="color: #999; font-size: 14px; line-height: 1.6; margin: 20px 0 0 0;">
                Si tenés alguna pregunta, no dudes en contactarnos.
            </p>
        `;
        return baseTemplate('Bienvenido a PublicaloGratis', content);
    },

    /**
     * Aviso approved notification
     */
    avisoApproved: (data: EmailTemplateData) => {
        const content = `
            <h2 style="color: #28a745; margin: 0 0 20px 0;">✅ ¡Tu aviso fue aprobado!</h2>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Hola ${data.userName},
            </p>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Tenemos excelentes noticias: tu aviso <strong>"${data.avisoTitulo}"</strong> ha sido aprobado y ya está visible para todos los usuarios.
            </p>
            <div style="background-color: #f8f9fa; border-left: 4px solid #28a745; padding: 16px; margin: 20px 0;">
                <p style="margin: 0; color: #666; font-size: 14px;">
                    💡 <strong>Tip:</strong> Aprovechá las primeras 24 horas para compartir tu aviso en redes sociales y maximizar su alcance.
                </p>
            </div>
            ${button('Ver Mi Aviso', data.avisoLink || '#', '#28a745')}
        `;
        return baseTemplate('Aviso Aprobado', content);
    },

    /**
     * Aviso rejected notification
     */
    avisoRejected: (data: EmailTemplateData) => {
        const content = `
            <h2 style="color: #dc3545; margin: 0 0 20px 0;">❌ Aviso no aprobado</h2>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Hola ${data.userName},
            </p>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Lamentamos informarte que tu aviso <strong>"${data.avisoTitulo}"</strong> no pudo ser aprobado.
            </p>
            ${data.razon ? `
                <div style="background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 16px; margin: 20px 0;">
                    <p style="margin: 0; color: #856404; font-size: 14px;">
                        <strong>Motivo:</strong> ${data.razon}
                    </p>
                </div>
            ` : ''}
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 20px 0 16px 0;">
                Podés editar tu aviso y volver a enviarlo para revisión siguiendo nuestras <a href="https://publicalogratis.com/terminos" style="color: #667eea;">políticas de publicación</a>.
            </p>
            ${button('Ver Mis Avisos', 'https://publicalogratis.com/mis-avisos', '#dc3545')}
        `;
        return baseTemplate('Aviso no aprobado', content);
    },

    /**
     * Payment confirmation
     */
    paymentConfirmation: (data: EmailTemplateData) => {
        const content = `
            <h2 style="color: #28a745; margin: 0 0 20px 0;">💰 Pago Confirmado</h2>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Hola ${data.userName},
            </p>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Confirmamos que hemos recibido tu pago de <strong>$${data.monto?.toFixed(2)}</strong> por <strong>${data.concepto}</strong>.
            </p>
            <table style="width: 100%; background-color: #f8f9fa; border-radius: 8px; margin: 20px 0; padding: 20px;">
                <tr>
                    <td style="padding: 8px 0; color: #666; font-size: 14px;">
                        <strong>Concepto:</strong>
                    </td>
                    <td style="padding: 8px 0; color: #333; font-size: 14px; text-align: right;">
                        ${data.concepto}
                    </td>
                </tr>
                <tr>
                    <td style="padding: 8px 0; color: #666; font-size: 14px;">
                        <strong>Monto:</strong>
                    </td>
                    <td style="padding: 8px 0; color: #28a745; font-size: 18px; font-weight: bold; text-align: right;">
                        $${data.monto?.toFixed(2)}
                    </td>
                </tr>
                <tr>
                    <td style="padding: 8px 0; color: #666; font-size: 14px;">
                        <strong>Fecha:</strong>
                    </td>
                    <td style="padding: 8px 0; color: #333; font-size: 14px; text-align: right;">
                        ${new Date().toLocaleDateString('es-AR')}
                    </td>
                </tr>
            </table>
            <p style="color: #666; font-size: 14px; line-height: 1.6; margin: 20px 0 0 0;">
                Podés ver el detalle de esta transacción en tu historial de pagos.
            </p>
            ${button('Ver Mis Compras', 'https://publicalogratis.com/mis-compras')}
        `;
        return baseTemplate('Pago Confirmado', content);
    },

    /**
     * Password reset
     */
    passwordReset: (data: EmailTemplateData) => {
        const content = `
            <h2 style="color: #667eea; margin: 0 0 20px 0;">🔐 Restablecer Contraseña</h2>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Hola ${data.userName},
            </p>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Recibimos una solicitud para restablecer la contraseña de tu cuenta. Si no fuiste vos, podés ignorar este email.
            </p>
            <div style="background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 16px; margin: 20px 0;">
                <p style="margin: 0; color: #856404; font-size: 14px;">
                    ⚠️ Este enlace es válido solo por <strong>1 hora</strong>.
                </p>
            </div>
            ${button('Restablecer Contraseña', data.resetLink || '#')}
            <p style="color: #999; font-size: 14px; line-height: 1.6; margin: 20px 0 0 0;">
                Si no solicitaste este cambio, tu cuenta está segura y podés ignorar este mensaje.
            </p>
        `;
        return baseTemplate('Restablecer Contraseña', content);
    },

    /**
     * Newsletter template
     */
    newsletter: (data: EmailTemplateData) => {
        const footer = `
            <p style="margin: 0 0 10px 0; color: #6c757d; font-size: 14px;">
                © ${new Date().getFullYear()} PublicaloGratis. Todos los derechos reservados.
            </p>
            ${data.unsubscribeLink ? `
                <p style="margin: 0; color: #6c757d; font-size: 12px;">
                    <a href="${data.unsubscribeLink}" style="color: #6c757d; text-decoration: underline;">
                        Darme de baja de estos emails
                    </a>
                </p>
            ` : ''}
    unsubscribeLink?: string;
        `;
        return baseTemplate('Newsletter', data.content || '', footer);
    },

    /**
     * Store Access Request (To Owner)
     */
    storeAccessRequest: (data: EmailTemplateData) => {
        const content = `
            <h2 style="color: #667eea; margin: 0 0 20px 0;">🔒 Nueva Solicitud de Acceso</h2>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Hola,
            </p>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                El usuario <strong>${data.requesterName}</strong> (${data.requesterEmail}) ha solicitado acceso a tu tienda privada <strong>${data.storeName}</strong>.
            </p>
            ${data.message ? `
                <div style="background-color: #f8f9fa; border-left: 4px solid #667eea; padding: 16px; margin: 20px 0;">
                    <p style="margin: 0; color: #666; font-size: 14px;">
                        <strong>Mensaje:</strong> ${data.message}
                    </p>
                </div>
            ` : ''}
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                ${data.whatsapp ? `📱 WhatsApp: ${data.whatsapp}` : ''}
            </p>
            ${button('Gestionar Solicitud', 'https://publicalogratis.com/mi-tienda/configuracion', '#667eea')}
        `;
        return baseTemplate('Solicitud de Acceso', content);
    },

    /**
     * Store Access Approved (To User)
     */
    storeAccessApproved: (data: EmailTemplateData) => {
        const content = `
            <h2 style="color: #28a745; margin: 0 0 20px 0;">✅ ¡Acceso Aprobado!</h2>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Hola ${data.requesterName},
            </p>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Tu solicitud de acceso a la tienda privada <strong>"${data.storeName}"</strong> ha sido aceptada.
            </p>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Ahora podés ver todos los productos y precios exclusivos.
            </p>
            ${button('Ir a la Tienda', `https://publicalogratis.com/tienda/${data.storeName?.toLowerCase().replace(/\s+/g, '-')}`, '#28a745')}
        `;
        return baseTemplate('Acceso Aprobado', content);
    },

    /**
     * Store Access Rejected (To User)
     */
    storeAccessRejected: (data: EmailTemplateData) => {
        const content = `
            <h2 style="color: #dc3545; margin: 0 0 20px 0;">❌ Solicitud de Acceso Rechazada</h2>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Hola ${data.requesterName},
            </p>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Lamentamos informarte que tu solicitud de acceso a la tienda <strong>"${data.storeName}"</strong> no ha sido aprobada por el administrador en este momento.
            </p>
        `;
        return baseTemplate('Acceso Rechazado', content);
    },

    /**
     * Order Status Update (To Customer)
     */
    orderStatusUpdate: (data: EmailTemplateData) => {
        let title = `Actualización de Pedido #${data.avisoTitulo}`; // reusing avisoTitulo as orderNumber
        let color = '#667eea';
        let statusText = '';

        if (data.razon === 'confirmado') {
            title = `¡Pedido #${data.avisoTitulo} Confirmado!`;
            color = '#28a745';
            statusText = 'confirmado';
        } else if (data.razon === 'enviado') {
            title = `¡Pedido #${data.avisoTitulo} Enviado! 🚚`;
            color = '#17a2b8';
            statusText = 'enviado';
        } else if (data.razon === 'entregado') {
            title = `¡Pedido #${data.avisoTitulo} Entregado! 🎉`;
            color = '#28a745';
            statusText = 'entregado';
        } else if (data.razon === 'cancelado') {
            title = `Pedido #${data.avisoTitulo} Cancelado`;
            color = '#dc3545';
            statusText = 'cancelado';
        } else if (data.razon === 'preparando') {
            title = `Pedido #${data.avisoTitulo} en Preparación`;
            color = '#6f42c1';
            statusText = 'en preparación';
        }

        const content = `
            <h2 style="color: ${color}; margin: 0 0 20px 0;">${title}</h2>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Hola ${data.userName},
            </p>
            <p style="color: #666; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">
                Te informamos que el estado de tu pedido <strong>#${data.avisoTitulo}</strong> en <strong>${data.storeName}</strong> ha cambiado a: <strong>${statusText.toUpperCase()}</strong>.
            </p>
            <div style="background-color: #f8f9fa; border-radius: 8px; padding: 16px; margin: 20px 0;">
                 <table width="100%" cellpadding="0" cellspacing="0">
                    <tr>
                        <td style="color: #666; font-size: 14px; padding-bottom: 8px;">Tienda:</td>
                        <td style="color: #333; font-size: 14px; font-weight: bold; padding-bottom: 8px; text-align: right;">${data.storeName}</td>
                    </tr>
                    <tr>
                        <td style="color: #666; font-size: 14px;">Total:</td>
                        <td style="color: ${color}; font-size: 16px; font-weight: bold; text-align: right;">$${data.monto}</td>
                    </tr>
                 </table>
            </div>
            <p style="color: #666; font-size: 14px; line-height: 1.6; margin: 0 0 16px 0;">
                Si tienes alguna duda, puedes contactar directamente con la tienda.
            </p>
            ${button('Ver Detalle del Pedido', 'https://publicalogratis.com/mi-perfil/compras', color)}
        `;
        return baseTemplate(title, content);
    }
};

export default emailTemplates;
